import { getRandomElementNoMatch, randomBooleanNoMatch } from '@frontend/demo-interceptors';
import {
  AnalysisTypeEnum,
  AppointmentTypeEnum,
  CallEditableFields,
  CallEditedField,
  CallRecord,
  CategoryEnum,
  ContactTypeEnum,
  GetCallEditHistoryResponse,
  OfficeUser,
  SentimentEnum,
} from '../types';

const randomEditedAt = () => new Date().toISOString();

const MockSentiments: SentimentEnum[] = [
  SentimentEnum.SENTIMENT_NEGATIVE,
  SentimentEnum.SENTIMENT_NEUTRAL,
  SentimentEnum.SENTIMENT_POSITIVE,
];

export const MockCategories: CategoryEnum[] = [
  CategoryEnum.CATEGORY_BILLING,
  CategoryEnum.CATEGORY_EMERGENCY,
  CategoryEnum.CATEGORY_COMPLAINT,
  CategoryEnum.CATEGORY_SCHEDULING,
];

export const MockContactTypes: ContactTypeEnum[] = [
  ContactTypeEnum.CONTACT_EXISTING_PATIENT,
  ContactTypeEnum.CONTACT_NEW_PATIENT,
  ContactTypeEnum.CONTACT_NOT_A_PATIENT,
];

export function getMockCallEditHistory(
  call: CallRecord,
  restoredFields: AnalysisTypeEnum[] = []
): GetCallEditHistoryResponse {
  const editedFlags = call.isFieldEdited || {
    isAppointmentTypeEdited: false,
    isCategoryEdited: false,
    isContactTypeEdited: false,
    isSchedulingOpportunityEdited: false,
    isSchedulingOutcomeEdited: false,
    isSentimentEdited: false,
    isServiceQualityFlagEdited: false,
  };

  const editedByUser = call.officeUser;

  const original: CallEditableFields = {};
  const current: CallEditedField[] = [];

  const pushCurrentEdit = (analysisType: AnalysisTypeEnum, fields: Partial<CallEditableFields>) => {
    current.push({
      fields: { ...fields },
      analysisType,
      editedBy: editedByUser as OfficeUser,
      editedAt: randomEditedAt(),
    });
  };

  const shouldRestore = (analysisType: AnalysisTypeEnum) => restoredFields.includes(analysisType);

  if (editedFlags.isContactTypeEdited) {
    const analysisType = AnalysisTypeEnum.ANALYSIS_TYPE_CONTACT_TYPE;

    const currentValue = call.contactType ?? ContactTypeEnum.CONTACT_UNKNOWN;

    const originalValue = shouldRestore(analysisType)
      ? currentValue
      : getRandomElementNoMatch(MockContactTypes, currentValue);

    original.contactType = originalValue;

    pushCurrentEdit(AnalysisTypeEnum.ANALYSIS_TYPE_CONTACT_TYPE, {
      contactType: currentValue,
    });
  }

  if (editedFlags.isSentimentEdited) {
    const analysisType = AnalysisTypeEnum.ANALYSIS_TYPE_SENTIMENT;

    const currentValue = call.callSentiment?.sentiment ?? null;

    const originalValue = shouldRestore(analysisType)
      ? currentValue
      : call.contactType === ContactTypeEnum.CONTACT_NOT_A_PATIENT
      ? null
      : getRandomElementNoMatch(MockSentiments, currentValue);

    original.callSentiment = originalValue === null ? null : { sentiment: originalValue };

    pushCurrentEdit(AnalysisTypeEnum.ANALYSIS_TYPE_SENTIMENT, {
      callSentiment: currentValue === null ? null : { sentiment: currentValue },
    });
  }

  if (editedFlags.isSchedulingOpportunityEdited) {
    const analysisType = AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OPPORTUNITY;

    const currentValue = call.callSchedulingOpportunity;

    const originalValue = shouldRestore(analysisType)
      ? currentValue
      : call.contactType === ContactTypeEnum.CONTACT_NOT_A_PATIENT
      ? null
      : randomBooleanNoMatch(!!currentValue);

    original.schedulingOpportunity = originalValue;

    pushCurrentEdit(AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OPPORTUNITY, {
      schedulingOpportunity: currentValue,
    });
  }

  if (editedFlags.isSchedulingOutcomeEdited) {
    const analysisType = AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OUTCOME;

    const currentValue = call.callSchedulingOutcome;

    const originalValue = shouldRestore(analysisType)
      ? currentValue
      : call.contactType === ContactTypeEnum.CONTACT_NOT_A_PATIENT
      ? null
      : randomBooleanNoMatch(!!currentValue);

    original.schedulingOutcome = originalValue;

    pushCurrentEdit(AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OUTCOME, {
      schedulingOutcome: currentValue,
    });
  }

  if (editedFlags.isCategoryEdited) {
    const analysisType = AnalysisTypeEnum.ANALYSIS_TYPE_CATEGORY;

    const currentValue = call.categories ?? [];

    const candidate = shouldRestore(analysisType)
      ? currentValue[0]
      : getRandomElementNoMatch(MockCategories, currentValue[0]);

    original.categories = candidate ? [candidate] : [];

    pushCurrentEdit(AnalysisTypeEnum.ANALYSIS_TYPE_CATEGORY, {
      categories: currentValue,
    });
  }

  if (editedFlags.isAppointmentTypeEdited) {
    const analysisType = AnalysisTypeEnum.ANALYSIS_TYPE_APPOINTMENT_TYPE;
    const currentValue = call.appointmentTypes ?? null;

    const originalValue = shouldRestore(analysisType)
      ? currentValue
      : call.contactType === ContactTypeEnum.CONTACT_NOT_A_PATIENT
      ? null
      : [
          getRandomElementNoMatch(
            Object.values(AppointmentTypeEnum),
            Array.isArray(currentValue) ? currentValue[0] : undefined
          ),
        ];

    original.appointmentTypes =
      originalValue === null
        ? null
        : {
            appointmentType: originalValue,
          };
    pushCurrentEdit(AnalysisTypeEnum.ANALYSIS_TYPE_APPOINTMENT_TYPE, {
      appointmentTypes: currentValue ? { appointmentType: currentValue } : null,
    });
  }

  return { original, current };
}
